/**
 * SupabaseManager - Supabase 클라우드 연동 관리
 * 사용자 인증 및 프로젝트 클라우드 저장/불러오기
 */

import { eventBus } from '../utils/EventBus.js';
import { stateManager } from './StateManager.js';

// Supabase 설정 (하드코딩)
const SUPABASE_URL = 'https://lufbotdmhgsuvejlytgh.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imx1ZmJvdGRtaGdzdXZlamx5dGdoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYzMDY1NzUsImV4cCI6MjA4MTg4MjU3NX0.JMzU8SiR8jb39xcRe4ySQSvZJButJP8OeCqOMDkNbRI';

// 관리자 이메일
const ADMIN_EMAIL = 'fkv777@gmail.com';

class SupabaseManager {
  constructor() {
    this.supabase = null;
    this.user = null;
    this.isConfigured = false;
    this.profileCache = null; // 헤더 표시용 프로필 캐시 (닉네임 등)
  }

  /**
   * Supabase 클라이언트 초기화
   */
  async init() {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      console.log('Supabase 설정이 없습니다. 클라우드 기능이 비활성화됩니다.');
      return false;
    }

    try {
      // Supabase JS 라이브러리 동적 로드
      if (!window.supabase) {
        await this.loadSupabaseScript();
      }

      this.supabase = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
      this.isConfigured = true;

      // 현재 세션 확인
      const { data: { session } } = await this.supabase.auth.getSession();
      if (session) {
        this.user = session.user;
        eventBus.emit('auth:login', { user: this.user });
      }

      // 인증 상태 변경 리스너
      this.supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_IN') {
          this.user = session.user;
          eventBus.emit('auth:login', { user: this.user });
        } else if (event === 'SIGNED_OUT') {
          this.user = null;
          this.profileCache = null;
          eventBus.emit('auth:logout', {});
        }
      });

      console.log('Supabase 초기화 완료');
      return true;
    } catch (error) {
      console.error('Supabase 초기화 실패:', error);
      return false;
    }
  }

  /**
   * Supabase JS 스크립트 동적 로드
   */
  loadSupabaseScript() {
    return new Promise((resolve, reject) => {
      if (window.supabase) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Supabase 설정 저장
   */
  configure(url, anonKey) {
    localStorage.setItem('eGIS_supabaseUrl', url);
    localStorage.setItem('eGIS_supabaseKey', anonKey);
    window.location.reload(); // 설정 적용을 위해 새로고침
  }

  /**
   * 설정 여부 확인
   */
  isSupabaseConfigured() {
    return !!(SUPABASE_URL && SUPABASE_ANON_KEY);
  }

  /**
   * 로그인 상태 확인
   */
  isLoggedIn() {
    return !!this.user;
  }

  /**
   * 현재 사용자 정보
   */
  getUser() {
    return this.user;
  }

  /**
   * 관리자 여부 확인
   */
  isAdmin() {
    return this.user && this.user.email === ADMIN_EMAIL;
  }

  // ==================== 인증 ====================

  /**
   * 이메일/비밀번호로 회원가입
   */
  async signUp(email, password) {
    if (!this.supabase) throw new Error('Supabase가 설정되지 않았습니다.');

    const { data, error } = await this.supabase.auth.signUp({
      email,
      password
    });

    if (error) throw error;
    return data;
  }

  /**
   * 이메일/비밀번호로 로그인
   */
  async signIn(email, password) {
    if (!this.supabase) throw new Error('Supabase가 설정되지 않았습니다.');

    const { data, error } = await this.supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    this.user = data.user;
    return data;
  }

  /**
   * Google OAuth 로그인
   */
  async signInWithGoogle() {
    if (!this.supabase) throw new Error('Supabase가 설정되지 않았습니다.');

    const { data, error } = await this.supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    });

    if (error) throw error;
    return data;
  }

  /**
   * 로그아웃
   */
  async signOut() {
    if (!this.supabase) return;

    const { error } = await this.supabase.auth.signOut();
    if (error) throw error;
    this.user = null;
    this.profileCache = null;
  }

  /**
   * 로컬 세션만 지우는 로그아웃 — 계정이 서버에서 이미 삭제돼
   * 일반 로그아웃(서버 세션 폐기)이 실패할 때 쓴다.
   */
  async signOutLocal() {
    if (!this.supabase) return;

    await this.supabase.auth.signOut({ scope: 'local' });
    this.user = null;
    this.profileCache = null;
  }

  /**
   * 비밀번호 변경
   */
  async updatePassword(newPassword) {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { error } = await this.supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;
    return true;
  }

  /**
   * 사용자 메타데이터 업데이트 (동의 정보 등)
   */
  async updateUserMetadata(metadata) {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { data, error } = await this.supabase.auth.updateUser({
      data: metadata
    });

    if (error) throw error;

    // 로컬 user 객체도 업데이트
    if (data.user) {
      this.user = data.user;
    }

    return data;
  }

  /**
   * 회원가입 (동의 정보 포함)
   */
  async signUpWithConsent(email, password, consentData) {
    if (!this.supabase) throw new Error('Supabase가 설정되지 않았습니다.');

    const { data, error } = await this.supabase.auth.signUp({
      email,
      password,
      options: {
        data: consentData
      }
    });

    if (error) throw error;
    return data;
  }

  // ==================== 프로필 관리 ====================

  /**
   * 헤더 등 상시 표시 영역에서 쓸 프로필을 미리 읽어 캐시에 담는다.
   * 프로필 미작성·네트워크 오류는 예외로 올리지 않고 캐시를 비운 채 넘어간다.
   */
  async loadProfileCache() {
    if (!this.supabase || !this.user) {
      this.profileCache = null;
      return null;
    }

    try {
      this.profileCache = await this.getProfile();
    } catch (error) {
      console.warn('프로필 로드 실패:', error);
      this.profileCache = null;
    }
    return this.profileCache;
  }

  /**
   * 캐시된 닉네임 (설정 전이면 null)
   */
  getNickname() {
    const nickname = this.profileCache?.nickname?.trim();
    return nickname || null;
  }

  /**
   * 사용자 프로필 가져오기
   */
  async getProfile() {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { data, error } = await this.supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', this.user.id)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116: no rows found
    return data;
  }

  /**
   * 프로필 저장/업데이트
   */
  async saveProfile(profileData) {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { data, error } = await this.supabase
      .from('user_profiles')
      .upsert({
        user_id: this.user.id,
        name: profileData.name,
        nickname: profileData.nickname,
        region: profileData.region,
        school: profileData.school,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      })
      .select();

    if (error) throw error;

    // 헤더의 닉네임 표시가 저장 즉시 반영되도록 캐시도 함께 갱신
    this.profileCache = { ...(this.profileCache || {}), ...profileData };
    eventBus.emit('auth:profile-updated', { profile: this.profileCache });

    return data;
  }

  /**
   * 학교별 사용자 통계 가져오기
   */
  async getSchoolStats() {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { data, error } = await this.supabase
      .from('user_profiles')
      .select('school, created_at')
      .not('school', 'is', null)
      .not('school', 'eq', '');

    if (error) throw error;

    // 학교별 집계 (사용자 수 + 최초 가입 시각)
    const stats = {};
    data.forEach(row => {
      if (!row.school) return;
      if (!stats[row.school]) {
        stats[row.school] = { count: 0, firstJoinedAt: null };
      }
      stats[row.school].count += 1;
      // 가장 빠른 가입 시각 추적 (동석차 정렬용)
      const joinedAt = row.created_at || null;
      if (joinedAt && (!stats[row.school].firstJoinedAt || joinedAt < stats[row.school].firstJoinedAt)) {
        stats[row.school].firstJoinedAt = joinedAt;
      }
    });

    // 순위 정렬: 사용자 수 내림차순 → 동석차는 첫 가입자가 빠른 학교 우선
    const ranked = Object.entries(stats)
      .map(([school, info]) => ({ school, count: info.count, firstJoinedAt: info.firstJoinedAt }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        // 동석차: 첫 가입자가 빠른 순 (가입 시각 없는 학교는 뒤로)
        if (!a.firstJoinedAt && !b.firstJoinedAt) return 0;
        if (!a.firstJoinedAt) return 1;
        if (!b.firstJoinedAt) return -1;
        if (a.firstJoinedAt < b.firstJoinedAt) return -1;
        if (a.firstJoinedAt > b.firstJoinedAt) return 1;
        return 0;
      });

    // 동석차 순위 부여 (같은 사용자 수 → 같은 순위, 예: 1, 1, 3)
    let rank = 0;
    let prevCount = null;
    ranked.forEach((item, i) => {
      if (item.count !== prevCount) {
        rank = i + 1;
        prevCount = item.count;
      }
      item.rank = rank;
    });

    return ranked;
  }

  /**
   * 내 학교 사용자 수 가져오기
   */
  async getMySchoolCount() {
    const profile = await this.getProfile();
    if (!profile || !profile.school) return null;

    const stats = await this.getSchoolStats();
    const mySchool = stats.find(s => s.school === profile.school);

    return {
      school: profile.school,
      count: mySchool ? mySchool.count : 0,
      rank: mySchool ? mySchool.rank : null,
      totalSchools: stats.length
    };
  }

  // ==================== 관리자: 회원 관리 ====================

  /**
   * 가입 회원 목록 조회 (관리자 전용)
   * 이메일은 auth.users 에 있어 서버 함수(admin_list_members)로 조인해 반환한다.
   * 프로필 미작성 회원도 함께 반환됨(name/nickname/region/school 은 null 가능).
   * @returns {Promise<Array<{name, email, nickname, region, school, created_at}>>}
   */
  async listMembers() {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }
    if (!this.isAdmin()) {
      throw new Error('관리자만 접근할 수 있습니다.');
    }

    const { data, error } = await this.supabase.rpc('admin_list_members');
    if (error) throw error;
    return data || [];
  }

  /**
   * 회원 프로필 수정 (관리자 전용) — 이름/닉네임/지역/학교
   */
  async updateMemberProfile(userId, { name, nickname, region, school }) {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }
    if (!this.isAdmin()) {
      throw new Error('관리자만 접근할 수 있습니다.');
    }

    const { error } = await this.supabase.rpc('admin_update_member_profile', {
      target_user_id: userId,
      p_name: name || null,
      p_nickname: nickname || null,
      p_region: region || null,
      p_school: school || null
    });
    if (error) throw error;
    return true;
  }

  /**
   * 회원 강제 탈퇴 (관리자 전용) — 개인정보 + 계정 완전 삭제, 되돌릴 수 없음
   */
  async deleteMember(userId) {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }
    if (!this.isAdmin()) {
      throw new Error('관리자만 접근할 수 있습니다.');
    }

    const { error } = await this.supabase.rpc('admin_delete_member', {
      target_user_id: userId
    });
    if (error) throw error;
    return true;
  }

  /**
   * 현재 로그인 계정의 비밀번호 확인 (재인증)
   * 위험한 작업(강제 탈퇴) 전 본인 확인용. 실패해도 현재 세션은 유지된다.
   * @returns {Promise<boolean>} 비밀번호 일치 여부
   */
  async verifyPassword(password) {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }
    if (!password) return false;

    const { error } = await this.supabase.auth.signInWithPassword({
      email: this.user.email,
      password
    });
    return !error;
  }

  // ==================== 프로젝트 저장/불러오기 ====================

  /**
   * 프로젝트를 클라우드에 저장
   */
  async saveProject(projectName, projectData) {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { data, error } = await this.supabase
      .from('projects')
      .upsert({
        user_id: this.user.id,
        name: projectName,
        data: projectData,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,name'
      })
      .select();

    if (error) throw error;
    return data;
  }

  /**
   * 사용자의 프로젝트 목록 가져오기
   */
  async listProjects() {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { data, error } = await this.supabase
      .from('projects')
      .select('id, name, updated_at')
      .eq('user_id', this.user.id)
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  /**
   * 프로젝트 불러오기
   */
  async loadProject(projectId) {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { data, error } = await this.supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .eq('user_id', this.user.id)
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * 프로젝트 삭제
   */
  async deleteProject(projectId) {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { error } = await this.supabase
      .from('projects')
      .delete()
      .eq('id', projectId)
      .eq('user_id', this.user.id);

    if (error) throw error;
    return true;
  }

  /**
   * 사용자의 모든 프로젝트 삭제 (회원 탈퇴용)
   */
  async deleteAllProjects() {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { error } = await this.supabase
      .from('projects')
      .delete()
      .eq('user_id', this.user.id);

    if (error) throw error;
    return true;
  }

  /**
   * 사용자 프로필 삭제 (회원 탈퇴용)
   */
  async deleteProfile() {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { error } = await this.supabase
      .from('user_profiles')
      .delete()
      .eq('user_id', this.user.id);

    if (error && error.code !== 'PGRST116') throw error;
    return true;
  }

  /**
   * 회원 탈퇴 — 계정 완전 삭제
   *
   * 클라이언트 키로는 auth.users 를 지울 수 없어 서버 함수(supabase-delete-own-account.sql)
   * 에 맡긴다. 프로젝트·프로필·게시한 스토리맵·계정을 한 트랜잭션으로 지우므로
   * 절반만 지워진 채 남는 일이 없다.
   */
  async deleteAccount() {
    if (!this.supabase || !this.user) {
      throw new Error('로그인이 필요합니다.');
    }

    const { error } = await this.supabase.rpc('delete_own_account');
    if (error) throw error;
    return true;
  }

  /**
   * 현재 상태를 클라우드에 동기화
   */
  async syncToCloud(projectName = '자동 저장') {
    const projectData = await stateManager.exportProject();
    return await this.saveProject(projectName, projectData);
  }

  /**
   * 클라우드에서 상태 복원
   */
  async syncFromCloud(projectId) {
    const project = await this.loadProject(projectId);
    if (project && project.data) {
      await stateManager.importProject(project.data);
      return true;
    }
    return false;
  }
}

export const supabaseManager = new SupabaseManager();
